import { useEffect, useState } from "react";

import { api, errorMessage } from "../api/client";
import { formatCount } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { useTransientStatus } from "../lib/useTransientStatus";
import { ui } from "../lib/ui";
import type { Branch, StaffMember } from "../types";
import { AsyncBlock } from "./AsyncBlock";
import { Panel } from "./Panel";

function StaffPhoto({
  branchId,
  person,
}: {
  branchId: string;
  person: StaffMember;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(null);
    setFailed(false);
    if (!person.face_crop_available) return;

    let active = true;
    let objectUrl: string | null = null;
    api
      .getStaffFaceCrop(branchId, person.id)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (active) setSrc(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [branchId, person.face_crop_available, person.id]);

  if (!person.face_crop_available) {
    return <span className="text-xs text-muted">No crop</span>;
  }
  if (failed) {
    return <span className="text-xs text-danger">Unavailable</span>;
  }
  if (!src) {
    return <span className="text-xs text-muted">Loading…</span>;
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" title="Open staff crop">
      <img
        src={src}
        alt={`${person.name} staff crop`}
        className="h-16 w-16 rounded-md border border-border object-cover"
      />
    </a>
  );
}

/** One branch's enrolled staff — the consented face gallery every stored
 *  track face is matched against. A match links the track and moves its
 *  entry into the staff count; nothing is removed from the totals.
 *
 *  Enrollment is one click on a face crop in the Tracks viewer. Removing a
 *  person permanently deletes their stored signature and crop, unlinks
 *  their tracks and counts those entries as customers again. */

export function StaffPanel({
  branch,
  onClose,
}: {
  branch: Branch;
  onClose: () => void;
}) {
  const { data, error, loading, reload } = useAsync(
    () => api.listStaff(branch.id),
    [branch.id],
  );
  const staff = data ?? [];

  const [status, setStatus] = useTransientStatus();
  const [actionError, setActionError] = useState<string | null>(null);

  const fail = (err: unknown) => setActionError(errorMessage(err));

  const saveField = async (
    person: StaffMember,
    field: "name" | "note",
    value: string,
  ) => {
    if (value === person[field]) return;
    if (field === "name" && !value) return;
    setActionError(null);
    try {
      await api.patchStaff(branch.id, person.id, { [field]: value });
      reload();
    } catch (err) {
      fail(err);
    }
  };

  const remove = async (person: StaffMember) => {
    const confirmed = window.confirm(
      `Remove ${person.name} from ${branch.name}'s enrolled staff?\n\n` +
        "Their stored face signature and crop are permanently deleted (the consent-" +
        `withdrawal path). Their ${formatCount(person.track_count)} linked track${
          person.track_count === 1 ? "" : "s"
        } are unlinked and their entries count as customers again.`,
    );
    if (!confirmed) return;
    setActionError(null);
    try {
      const applied = await api.deleteStaff(branch.id, person.id);
      setStatus(
        `Removed ${person.name}: ${formatCount(applied.tracks)} track${
          applied.tracks === 1 ? "" : "s"
        } unlinked, ${formatCount(applied.entries)} entries count as customers again.`,
      );
      reload();
    } catch (err) {
      fail(err);
    }
  };

  const totalEntries = staff.reduce((sum, person) => sum + person.match_count, 0);

  return (
    <Panel
      title={`Staff — ${branch.name}`}
      subtitle={
        "Enrolled employees are recognised from their stored face on every " +
        "processed day, past and future. Their entries stay in the totals and " +
        "are counted again as staff, so the dashboard can show either figure. " +
        "Mark someone by clicking a face crop in this branch's Tracks viewer; " +
        "names can be edited here. Removing a person permanently deletes their " +
        "stored face signature and crop."
      }
      actions={
        <button type="button" className={ui.btnQuiet} onClick={onClose}>
          Close
        </button>
      }
    >
      {status && <p className={`${ui.notice} ${ui.noticeSuccess}`}>{status}</p>}
      {actionError && <p className={`${ui.notice} ${ui.noticeError}`}>{actionError}</p>}

      <AsyncBlock
        loading={loading}
        error={error}
        empty={staff.length === 0}
        emptyMessage="No staff enrolled yet. Open this branch's Tracks viewer and click a face crop to mark that person as staff."
      >
        <p className="mb-3 text-xs text-muted">
          {staff.length} enrolled · {formatCount(totalEntries)} staff entries detected
          across processed days
        </p>
        <table className={ui.table}>
          <thead>
            <tr>
              <th>Photo</th>
              <th className="w-[22%]">Name</th>
              <th className="w-[30%]">Note</th>
              <th>Entries</th>
              <th>Tracks</th>
              <th>Enrolled</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {staff.map((person) => (
              <tr key={person.id}>
                <td>
                  <StaffPhoto branchId={branch.id} person={person} />
                </td>
                <td>
                  <EditableCell
                    key={`${person.id}:name:${person.name}`}
                    initial={person.name}
                    placeholder="Name"
                    strong
                    onSave={(value) => saveField(person, "name", value)}
                  />
                </td>
                <td>
                  <EditableCell
                    key={`${person.id}:note:${person.note}`}
                    initial={person.note}
                    placeholder="Add note…"
                    onSave={(value) => saveField(person, "note", value)}
                  />
                </td>
                <td>
                  {formatCount(person.match_count)}
                  {person.last_matched_day && (
                    <div className="text-xs text-muted">
                      last {person.last_matched_day}
                    </div>
                  )}
                </td>
                <td>
                  {formatCount(person.track_count)}
                  <div className="text-xs text-muted">
                    {formatCount(person.exit_count)} exit{person.exit_count === 1 ? "" : "s"}
                  </div>
                </td>
                <td className="whitespace-nowrap text-muted">
                  {person.created_at.slice(0, 10)}
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    className={ui.btnDanger}
                    onClick={() => remove(person)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AsyncBlock>
    </Panel>
  );
}

/** Inline text editor: local draft, saved on blur or Enter. Remounted (via
 *  key) whenever the server value changes, so a reload reseeds it. */
function EditableCell({
  initial,
  placeholder,
  strong = false,
  onSave,
}: {
  initial: string;
  placeholder: string;
  strong?: boolean;
  onSave: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <input
      className={`${ui.input} w-full ${strong ? "font-semibold" : ""}`}
      value={value}
      placeholder={placeholder}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onSave(value.trim())}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
    />
  );
}
